import {
  RequestFlow,
  requestFlow,
  requestOutcomeStatus,
  requestStatus,
  TotalLengthResponse,
} from 'src/lib/services/api/request-api';
import { generatePath } from 'react-router-dom';
import { appRoutes } from 'src/routes';
import { TFunction } from 'i18next';
import {
  hasUploadingPendingUpload,
  type BackgroundUploadRequest,
} from 'src/features/requests/background-upload/use-background-upload-store';

const totalLengthOtherValue = '-other-';

// A request is only locked for interaction while a file is actively
// transferring for it in *this* browser session — `activeUpload` is this
// request's entry in useBackgroundUploadStore, the client-side source of
// truth for that. A request stuck at status='uploading' with no local
// record (closed tab, different device, cleared storage) is NOT actively
// racing anything here, so it stays reachable rather than trapping the
// customer with no way back in (#2551). A record whose files have all
// failed also isn't actively transferring — that state needs the
// Retry/Dismiss controls inside RequestStatusCell instead of a lock (#2567).
const isRequestLocked: (
  request: { status?: string | any },
  activeUpload?: BackgroundUploadRequest,
) => boolean = (request, activeUpload) =>
  request?.status === requestStatus.uploading && hasUploadingPendingUpload(activeUpload);

const canEditRequest: (
  request: {
    revisions_count?: number;
    status?: string | any;
    requested_attention_at?: string | null;
  },
  activeUpload?: BackgroundUploadRequest,
) => boolean = (request, activeUpload) => {
  const { status, revisions_count, requested_attention_at } = request;
  // Multi-outcome projects lock per-outcome inside the edit modal (see
  // isOutcomeLocked), so this only gates opening the modal — a mixed-state
  // project is safe to open. (#2504 removed the old all-or-nothing lock.)

  // The upload lock always wins, even over the attention-flag override below
  // — opening the modal or navigating in now would race the active transfer
  // and corrupt request state (#2567).
  if (isRequestLocked(request, activeUpload)) {
    return false;
  }

  // Allow editing when request needs attention (even with revisions or in currently_editing status)
  if (requested_attention_at) {
    return true;
  }

  // uploading is included here deliberately: isRequestLocked above already
  // returned false, so this is either not uploading at all, or a stuck
  // upload with no active local transfer — safe to edit either way (#2551).
  return (
    !revisions_count &&
    [requestStatus.draft, requestStatus.queued, requestStatus.uploading].includes(status)
  );
};

// Per-outcome edit lock inside the multi-outcome Edit Request modal (#2504).
// Outcomes an editor is working on (Editing) or has delivered (Ready for
// Review / Approved / Archived) are read-only, and so is a Needs Re-edit
// outcome (#2504 reopen): it sits back in the queue, but the customer must
// not re-alter its config while a re-edit is pending. Plain Queued outcomes
// stay editable, and an attention flag always unlocks since it explicitly
// asks the customer for input.
const isOutcomeLocked: (outcome?: {
  status?: string | any;
  requested_attention_at?: string | null;
  requested_reedit_at?: string | null;
}) => boolean = (outcome) => {
  if (!outcome || outcome.requested_attention_at) {
    return false;
  }

  // Needs Re-edit = a queued row carrying the re-edit flag (mirrors
  // resolveDisplayedRequestStatus). Lock it like the in-progress statuses.
  if (outcome.requested_reedit_at && outcome.status === requestStatus.queued) {
    return true;
  }

  return [
    requestStatus.currentlyEditing,
    requestStatus.delivered,
    requestStatus.complete,
    requestOutcomeStatus.archived,
  ].includes(outcome.status);
};

const canViewOutcome: (request: {
  revisions_count?: number;
  status?: string | any;
  requested_attention_at?: string | null;
  requested_reedit_at?: string | null;
}) => boolean = (request) => {
  // A Needs Attention/Needs Re-edit outcome has its raw status flipped back
  // to queued (see resolveDisplayedRequestStatus/isOutcomeLocked), so it must
  // be checked before the status list below, or it reads as an unviewable
  // queued row with no revisions yet.
  if (request.requested_attention_at || request.requested_reedit_at) {
    return true;
  }

  // Editing / Ready-for-Review / Approved outcomes are viewable regardless of
  // whether a revision has been uploaded yet. Anything earlier (queued, draft,
  // uploading) stays non-clickable.
  if (
    [requestStatus.currentlyEditing, requestStatus.delivered, requestStatus.complete].includes(
      request.status,
    )
  ) {
    return true;
  }

  return !!request.revisions_count;
};

/** A request the client deleted: in Archived, read-only, never reopened (#2752). */
const isClientDeleted = (request?: { archive_reason?: string | null } | null): boolean =>
  request?.archive_reason === 'client_delete';

const getRequestNavigationRoute = (requestId: string, flow: RequestFlow, archiveReason?: string | null) => {
  // A deleted request never had a version, so the revisions page has nothing to
  // show; its request page carries the "Deleted by" banner and Duplicate.
  if (isClientDeleted({ archive_reason: archiveReason })) {
    return generatePath(appRoutes.request, { requestId });
  }

  switch (flow) {
    case requestFlow.outcome:
      return generatePath(appRoutes.requestRevisions, { requestId });
    case requestFlow.multiOutcome:
      return generatePath(appRoutes.request, { requestId });
    case requestFlow.repurpose:
      return generatePath(appRoutes.request, { requestId });
    default:
      return generatePath(appRoutes.requestRevisions, { requestId });
  }
};

const totalLengthToString = (totalLength?: TotalLengthResponse) => {
  if (!totalLength || !totalLength.value || !totalLength.unit) {
    return undefined;
  }

  return `up to ${totalLength.value} ${totalLength.unit}`;
};

// "Needs Attention" and "Needs Re-edit" used to live as separate substatus
// alerts beside the pill; now they override the pill itself. Attention is
// urgency-first (editor blocked) and wins over an in-flight re-edit cycle.
// Re-edit only overrides when the request is back in queued — once it moves
// to editing/ready-for-review the underlying status takes over and the
// loader-trail icon (added separately) signals the cycle is still in flight.
const resolveDisplayedRequestStatus = (request: {
  status?: string | null;
  requested_attention_at?: string | null;
  requested_reedit_at?: string | null;
  archived_at?: string | null;
}): string => {
  if (request.requested_attention_at) {
    return 'needs_attention';
  }
  if (request.requested_reedit_at && request.status === requestStatus.queued) {
    return 'needs_reedit';
  }
  // File Retention: approved requests are archived 30 days after approval. The
  // API keeps status "complete" (so they stay openable); the client sees Archived.
  if (request.archived_at && request.status === requestStatus.complete) {
    return 'archived';
  }
  return request.status ?? 'unknown';
};

// Loader trail = "this request has been touched by a re-edit at some point in
// its life and hasn't been Approved/Archived yet." Karina's spec across #2443
// and #2442: the icon must NOT appear on fresh requests an editor progressed
// normally (Queued → Editing → Ready for Review, no client re-edit demand),
// must persist through Needs Re-edit → Editing → back to Ready for Review,
// AND must survive a customer "Undo re-edit" so the row's tracking history
// stays visible (the Undo clears `requested_reedit_at` but the cycle still
// happened).
//
// `reedit_count` is the right signal — be-core's MayRequestReedit::setRequestedReedit
// ticks it up on every Re-edit click and nothing clears it. `requested_reedit_at`
// is the live "currently waiting on a re-edit" flag and we OR it in as a
// safety net so the loader still lights up on a row from before this counter
// was introduced (pre-deploy data has `reedit_count = 0` but the in-cycle
// timestamp set).
//
// Do NOT gate on revision-row counts. Earlier attempts used `revisions_count`
// (false positive — revision rows grow on editor-side paths the client never
// initiated) and `rated_revisions_count` (false negative — that counts star
// ratings from /revisions/{id}/rate, independent of requesting a re-edit, so
// a client who re-edits without rating never lit the loader).
const isInReeditCycle = (request: {
  status?: string | null;
  requested_reedit_at?: string | null;
  reedit_count?: number;
}): boolean => {
  const hasHistoricalReedit = (request.reedit_count ?? 0) > 0 || !!request.requested_reedit_at;

  return (
    hasHistoricalReedit &&
    request.status !== requestStatus.complete &&
    request.status !== requestOutcomeStatus.archived
  );
};

const requestStatusTranslationsMap = (status: string) => {
  switch (status) {
    case 'draft':
      return 'Draft';
    case 'uploading':
      return 'Uploading';
    case 'upload_failed':
      return 'Upload failed';
    case 'queued':
      return 'Queued';
    case 'currently_editing':
      return 'Editing';
    case 'delivered':
      return 'Ready for review';
    case 'needs_attention':
      return 'Needs Attention';
    case 'needs_reedit':
      return 'Needs Re-edit';
    case 'complete':
      return 'Approved';
    case requestOutcomeStatus.archived:
      return 'Archived';
    case 'all':
      return 'All';
    default:
      return 'Unknown';
  }
};

// Tab labels diverge from status pill labels: the "Queued" tab is presented as
// "Priority Queue" so clients understand they can reorder/edit, while the row
// status pill keeps reading "Queued".
const requestTabTranslationsMap = (tab: string) => {
  if (tab === 'queued') {
    return 'Priority Queue';
  }
  return requestStatusTranslationsMap(tab);
};

// URL ↔ internal tab token translation. The "Approved" tab in the URL maps to
// "complete" internally so older share links keep working; everything else is
// identity. Single source of truth — used by both the tabs UI (active state +
// onChange) and any caller that needs to deep-link into a tab (e.g. the
// post-submit redirect to Priority Queue from the request form modal).
const urlTabToInternalToken = (tab: string): string =>
  tab === 'approved' ? 'complete' : tab;

const internalToUrlTabToken = (tab: string): string =>
  tab === 'complete' ? 'approved' : tab;

export const formatDueDate: (t: TFunction, dueDate: string | Date) => string = (t, dueDate) => {
  // Parse as local date to prevent UTC-to-local timezone shift
  // e.g. "2026-03-16T01:00:00Z" in UTC-5 would show Mar 15 instead of Mar 16
  const dateStr = typeof dueDate === 'string' ? dueDate : dueDate.toISOString();
  const [year, month, day] = dateStr.split('T')[0].split('-').map(Number);
  const localDate = new Date(year, month - 1, day);

  // Safety net: if the date falls on a weekend, shift to next Monday
  const dayOfWeek = localDate.getDay();
  if (dayOfWeek === 6) localDate.setDate(localDate.getDate() + 2); // Saturday → Monday
  if (dayOfWeek === 0) localDate.setDate(localDate.getDate() + 1); // Sunday → Monday

  return t('Up to {{val, datetime}}', {
    val: localDate,
    formatParams: {
      val: { year: 'numeric', month: 'short', day: 'numeric' },
    },
  });
};

export const canDeleteRequest: (
  request: {
    revisions_count?: number;
    revisions?: any[];
    status?: string | any;
    assigned_editor?: unknown;
    editor?: unknown;
    archived_at?: string | null;
  },
  activeUpload?: BackgroundUploadRequest,
) => boolean = (request, activeUpload) => {
  // A genuinely stuck-uploading request (no active local transfer) is safe —
  // and useful — to delete, e.g. to clean up and start over. Only block
  // deletion while a file is actively writing to it in this session, which
  // could orphan S3 objects / attach calls against a since-deleted request
  // (#2567).
  if (isRequestLocked(request, activeUpload)) {
    return false;
  }
  // File Retention (#2752): deleting moves a request to Archived, once.
  if (request.archived_at) {
    return false;
  }
  if (request.revisions_count || request.revisions?.length) {
    return false;
  }
  // Editor assignment is the cut-off, not status: a Queued request that
  // already has an editor can't be deleted.
  if (request.status === requestStatus.queued) {
    return !request.assigned_editor && !request.editor;
  }
  return [requestStatus.uploading, requestStatus.draft].includes(request.status);
};

export const canViewRequest: (request: {
  revisions_count?: number;
  revisions?: any[];
  flow?: string | any;
  status?: string | any;
}) => boolean = (request) => {
  return (
    !!request.revisions_count ||
    !!request.revisions?.length ||
    ([requestFlow.multiOutcome, requestFlow.repurpose].includes(request.flow) &&
      ![requestStatus.draft, requestStatus.uploading, requestStatus.queued].includes(request.status))
  );
};

export {
  totalLengthOtherValue,
  canEditRequest,
  isOutcomeLocked,
  isRequestLocked,
  canViewOutcome,
  getRequestNavigationRoute,
  isClientDeleted,
  totalLengthToString,
  requestStatusTranslationsMap,
  requestTabTranslationsMap,
  resolveDisplayedRequestStatus,
  isInReeditCycle,
  urlTabToInternalToken,
  internalToUrlTabToken,
};
